// Barevné funkce pro generátor tématu (themeCss.ts). Vzorce odpovídají Sassu / Bootstrapu
// (`mix`, `shade-color`, `tint-color`, `color-contrast`), takže výchozí téma vychází stejně jako
// dnešní zkompilovaný Bootstrap — viz assets/styles/_tokens.scss.

export type Rgb = [number, number, number];

export const parseHex = (hex: string): Rgb => {
	const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
	if (!m) throw new Error(`Neplatná barva "${hex}", očekává se #rrggbb.`);
	const n = parseInt(m[1], 16);
	return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

export const toHex = ([r, g, b]: Rgb): string =>
	'#' + [r, g, b].map(v => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('');

/** Sass `mix($color1, $color2, $weight)` — `weight` (0–1) je podíl první barvy. */
export const mix = (color1: string, color2: string, weight: number): string => {
	const a = parseHex(color1), b = parseHex(color2);
	return toHex([0, 1, 2].map(i => a[i] * weight + b[i] * (1 - weight)) as Rgb);
};

/** Bootstrap `shade-color($color, $weight)` — míchání s černou. */
export const shade = (color: string, weight: number): string => mix('#000000', color, weight);
/** Bootstrap `tint-color($color, $weight)` — míchání s bílou. */
export const tint = (color: string, weight: number): string => mix('#ffffff', color, weight);

const channelLuminance = (v: number): number => {
	const s = v / 255;
	return s < 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

export const luminance = (color: string): number => {
	const [r, g, b] = parseHex(color);
	return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
};

/** WCAG kontrastní poměr (1–21). */
export const contrastRatio = (a: string, b: string): number => {
	const la = luminance(a), lb = luminance(b);
	return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

export const MIN_CONTRAST_RATIO = 4.5;

/**
 * Bootstrap `color-contrast($background)`: první z (bílá, černá), která má kontrast > 4.5,
 * jinak ta s nejvyšším kontrastem.
 */
export const contrastColor = (background: string): '#ffffff' | '#000000' => {
	const candidates = ['#ffffff', '#000000'] as const;
	let best: typeof candidates[number] = candidates[0];
	let bestRatio = 0;
	for (const c of candidates) {
		const ratio = contrastRatio(background, c);
		if (ratio > MIN_CONTRAST_RATIO) return c;
		if (ratio > bestRatio) { best = c; bestRatio = ratio; }
	}
	return best;
};

export const rgbTriplet = (color: string): string => parseHex(color).join(', ');

/**
 * Čitelná barva textu pro libovolné pozadí zadané uživatelem (`#rrggbb`, `#rrggbbaa`, `rgba(...)`),
 * např. u dlaždic dashboardu, které mají vlastní pozadí, ale ne barvu textu — jinak by v tmavém
 * režimu zůstal světlý text z tématu na světlém pozadí. `null` = pozadí je (téměř) průhledné nebo
 * ho nelze přečíst, platí barva textu z tématu.
 */
export const autoTextColor = (background: string): string | null => {
	const v = background.trim();
	let rgb: Rgb | null = null;
	let alpha = 1;
	let m = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(v);
	if (m) {
		rgb = parseHex('#' + m[1]);
		if (m[2]) alpha = parseInt(m[2], 16) / 255;
	} else if ((m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/i.exec(v))) {
		rgb = [+m[1], +m[2], +m[3]];
		if (m[4] !== undefined) alpha = +m[4];
	}
	if (!rgb || alpha < 0.5) return null;
	return contrastColor(toHex(rgb));
};
